import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaClipboardList, FaPlus, FaSearch, FaUserFriends } from 'react-icons/fa';
import CardHeader from '../../../components/Card/CardHeader';
import Input from '../../../components/Input/Input';
import Loader from '../../../components/Loader/Loader';
import Pagination from '../../../components/Pagination/Pagination';
import SurveyStatCard from './SurveyStatCard';
import SurveyCard from './SurveyCard';
import { getSurveys, getSurveyResults } from '../../../services/SurveyService';
import './SurveyList.css';

const SurveyList = () => {
  const permissions = JSON.parse(localStorage.getItem('permissions'));
  const [search, setSearch] = useState('');
  const [surveys, setSurveys] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  const fetchSurveys = useCallback(async ({ selected = 0 } = {}, searchTerm = '') => {
    try {
      setIsLoader(true);
      setCurrentPage(selected);
      const response = await getSurveys({ page: selected + 1, search: searchTerm });
      if (response.status === 200) {
        const rows = response.data.data || [];
        setLastPage(response.data.last_page || 1);

        const hydrated = await Promise.all(rows.map(async (row) => {
          try {
            const resultsResponse = await getSurveyResults(row.id);
            return { ...row, ...resultsResponse.data.data };
          } catch (error) {
            console.error('API Error:', error.response);
            return row;
          }
        }));
        setSurveys(hydrated);
      }
    } catch (error) {
      console.error('API Error:', error.response);
    } finally {
      setIsLoader(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSurveys({ selected: 0 }, search);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, fetchSurveys]);

  const stats = useMemo(() => {
    const imported = surveys.length;
    const active = surveys.filter((s) => Number(s.status) === 1).length;
    const targeted = surveys.reduce((sum, s) => sum + (s.targeted_students || 0), 0);
    const completed = surveys.reduce((sum, s) => sum + (s.completed_responses || 0), 0);
    const pending = surveys
      .filter((s) => Number(s.status) === 1)
      .reduce((sum, s) => sum + (s.pending ?? Math.max((s.targeted_students || 0) - (s.completed_responses || 0), 0)), 0);

    return [
      { title: 'Imported surveys', value: imported, subtext: `${active} active now`, icon: <FaClipboardList /> },
      { title: 'Students targeted', value: targeted, subtext: 'across all surveys', icon: <FaUserFriends /> },
      { title: 'Responses collected', value: completed, subtext: targeted > 0 ? `${Math.round((completed / targeted) * 100)}% overall` : '0% overall', icon: <FaClipboardList /> },
      { title: 'Pending nudges', value: pending, subtext: 'students yet to respond', solid: true, icon: <FaUserFriends /> },
    ];
  }, [surveys]);

  return (
    <div className="animated survey-page">
      <div className="row">
        {stats.map((stat) => (
          <SurveyStatCard key={stat.title} {...stat} />
        ))}
      </div>

      <div className="card survey-list-card">
        <CardHeader
          title="Imported Surveys"
          icon={<FaClipboardList />}
          button={
            <div className="survey-header-actions">
              <div className="survey-search-box">
                <FaSearch className="survey-search-icon" />
                <Input
                  type="text"
                  name="survey-search"
                  placeholder="Search surveys..."
                  value={search}
                  onChangeHandler={(e) => setSearch(e.target.value)}
                />
              </div>
              {permissions && permissions.includes('survey.create') && (
                <Link to="/import/survey" className="btn btn-primary">
                  <FaPlus className="me-1" /> Import from SurveyMonkey
                </Link>
              )}
            </div>
          }
        />
        <div className="card-body">
          <p className="survey-section-subtitle">
            Newest first · scoped to this org &amp; session · a{' '}
            <span className="survey-audience-chip inline">
              <FaUserFriends /> n students
            </span>{' '}
            tag means the survey was narrowed to named students of its class(es)
          </p>

          {isLoader ? <Loader /> : (
            <>
              <div className="survey-card-list">
                {surveys.length === 0 ? (
                  <div className="survey-empty-state">No surveys match your search.</div>
                ) : (
                  surveys.map((survey) => (
                    <SurveyCard
                      key={survey.id}
                      survey={survey}
                      onDeleted={() => fetchSurveys({ selected: currentPage }, search)}
                    />
                  ))
                )}
              </div>
              <Pagination
                totalPage={lastPage}
                currentPage={currentPage}
                handlePageChange={(i) => fetchSurveys(i, search)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyList;
